import React, { useEffect, useRef, useState } from 'react';
import { ImagePlus, Quote, Video, Link2, Plus, X, Pencil, Check, LoaderCircle, Send } from 'lucide-react';

import { parseYoutubeUrl } from '../lib/youtube-url';
import { uploadSettings } from '../lib/upload-settings';

import { profileHeaders } from './profileAuth';
import './AddMemory.css';
import ContributorFields from './ContributorFields';

const NOOP = () => {};
const MAX_IMAGE_BYTES = 12 * 1024 * 1024;
const MAX_VIDEO_BYTES = 512 * 1024 * 1024;
const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
const createId = () => window.crypto?.randomUUID?.() || `draft_${Date.now()}_${Math.random().toString(36).slice(2)}`;
const MODES = [
  ['image', 'Fotografie', ImagePlus],
  ['text', 'Příběh / citát', Quote],
  ['youtube', 'YouTube', Link2],
  ['video', 'Video', Video],
];

export default function AddMemory({ callback = NOOP, profileId = 1, profileSettings = {}, profileName = '', onBusyChange = NOOP, managed = false }) {
  const uploads = uploadSettings(profileSettings);
  const maxPhotos = uploads.photoBatchLimit || Infinity;
  const allowVideo = managed || uploads.allowVideoUploads;
  const [mode, setMode] = useState('image');
  const [textType, setTextType] = useState('story');
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState('');
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [author, setAuthor] = useState('');
  const [contributor, setContributor] = useState({ email: '', relationship: 'unspecified', relationship_detail: '', audience: 'public' });
  const [sendEditLink, setSendEditLink] = useState(false);
  const [mailAvailable, setMailAvailable] = useState(false);
  const contributorDirty = useRef(false);
  const [editingCaption, setEditingCaption] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const [selectionError, setSelectionError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const input = useRef(null);
  const photosRef = useRef([]);
  const videoRef = useRef(null);
  const videoInput = useRef(null);
  const videoPreview = useRef(null);
  const videoRequest = useRef(null);
  const sent = useRef(new Set());
  const pendingApproval = useRef(false);
  const draftKeys = useRef({ story: createId(), quotation: createId(), youtube: createId() });
  const mounted = useRef(true);
  const video = parseYoutubeUrl(youtubeUrl);
  const anonymous = profileSettings.allowAnonymous !== false;
  const remaining = photos.filter((photo) => !sent.current.has(photo.id));
  const ready = mode === 'image' ? remaining.length > 0 : mode === 'text' ? Boolean(text.trim()) : mode === 'video' ? allowVideo && Boolean(videoFile) : Boolean(video);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/profiles/${profileId}/contributor`, { signal: controller.signal, cache: 'no-store' }).then(response => response.json()).then(data => {
      setMailAvailable(data.mailAvailable === true);
      if (!contributorDirty.current) {
        setAuthor(data.defaults.author || '');
        setContributor(previous => ({ ...previous, ...data.defaults }));
      }
    }).catch(() => {});
    return () => controller.abort();
  }, [profileId]);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);
  useEffect(
    () => () => {
      mounted.current = false;
      photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview));
      if (videoPreview.current) URL.revokeObjectURL(videoPreview.current);
      videoRequest.current?.abort();
    },
    [],
  );
  useEffect(() => {
    if (!allowVideo && !busy && mode === 'video') setMode('image');
  }, [allowVideo, busy, mode]);
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const addPhotos = (files) => {
    if (busy) return;
    const accepted = [];
    const problems = [];
    const known = new Set(photos.map((photo) => `${photo.file.name}:${photo.file.size}:${photo.file.lastModified}`));
    let imageCount = photos.filter((photo) => photo.type === 'image').length;
    for (const file of files) {
      const type = /\.(mp4|m4v|webm)$/i.test(file.name) ? 'video' : IMAGE_TYPES.has(file.type) ? 'image' : null;
      if (!type) {
        problems.push(`${file.name}: ${allowVideo ? 'vyberte fotografii, MP4 nebo WebM.' : 'vyberte JPG, PNG, WebP nebo GIF.'}`);
        continue;
      }
      if (type === 'video' && !allowVideo) {
        problems.push(`${file.name}: nahrávání videí návštěvníky je vypnuté.`);
        continue;
      }
      if (file.size > (type === 'video' ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES)) {
        problems.push(`${file.name}: ${type === 'video' ? 'video je větší než 512 MB.' : 'fotografie je větší než 12 MB.'}`);
        continue;
      }
      const key = `${file.name}:${file.size}:${file.lastModified}`;
      if (known.has(key)) continue;
      if (type === 'image' && imageCount >= maxPhotos) {
        const problem = `Najednou lze přidat nejvýše ${maxPhotos} fotografií.`;
        if (!problems.includes(problem)) problems.push(problem);
        continue;
      }
      known.add(key);
      if (type === 'image') imageCount += 1;
      accepted.push({ id: createId(), file, type, preview: URL.createObjectURL(file), caption: '', status: 'idle' });
    }
    setPhotos((previous) => [...previous, ...accepted]);
    setSelectionError(problems.join(' '));
    setError('');
    setConfirmation('');
  };
  const removePhoto = (photo) => {
    if (sent.current.has(photo.id)) return;
    URL.revokeObjectURL(photo.preview);
    setPhotos((previous) => previous.filter((item) => item.id !== photo.id));
    setSelectionError('');
  };
  const mark = (id, status) => {
    if (mounted.current) setPhotos((previous) => previous.map((photo) => (photo.id === id ? { ...photo, status } : photo)));
  };
  const selectVideo = (file) => {
    setError('');
    if (!file) return;
    if (!/\.(mp4|m4v|webm)$/i.test(file.name)) return setError('Vyberte MP4 nebo WebM.');
    if (file.size > MAX_VIDEO_BYTES) return setError('Video může mít nejvýše 512 MB.');
    if (videoPreview.current) URL.revokeObjectURL(videoPreview.current);
    videoPreview.current = URL.createObjectURL(file);
    setVideoFile({ id: createId(), file, preview: videoPreview.current });
    setVideoTitle('');
    setConfirmation('');
    setVideoProgress(0);
  };
  const post = async (task) => {
    const form = new FormData();
    form.append('author', author.trim() || 'Anonym');
    form.append('display_author', author.trim() || 'Anonym');
    form.append('submission_key', task.id);
    for (const key of ['email', 'relationship', 'relationship_detail', 'audience']) form.append(key, contributor[key] || '');
    if (task.type === 'video') {
      form.append('title', task.short || '');
      form.append('long_description', task.long || '');
      form.append('video', task.file);
      setUploadingVideo(true);
      setVideoProgress(0);
      let data;
      try {
        data = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          videoRequest.current = xhr;
          xhr.open('POST', `/api/profiles/${profileId}/video-memories`);
          if (managed) for (const [name, value] of Object.entries(profileHeaders(profileId))) xhr.setRequestHeader(name, value);
          xhr.upload.onprogress = (event) => {
            if (mounted.current && event.lengthComputable) setVideoProgress(Math.round((event.loaded / event.total) * 100));
          };
          xhr.onload = () => {
            let result;
            try {
              result = JSON.parse(xhr.responseText);
            } catch {
              result = {};
            }
            if (xhr.status >= 200 && xhr.status < 300) resolve(result);
            else
              reject(
                new Error(
                  xhr.status === 403
                    ? 'Nahrávání videí návštěvníky je vypnuté.'
                    : xhr.status === 413
                      ? 'Video je příliš velké.'
                      : xhr.status === 429
                        ? 'Příliš mnoho nahrávání. Zkuste to později.'
                        : xhr.status === 409
                          ? 'Jiné video se právě zpracovává. Zkuste to za chvíli znovu.'
                          : 'Video se nepodařilo nahrát. Použijte MP4 (H.264/AAC) nebo WebM (VP8/VP9).',
                ),
              );
          };
          xhr.onerror = xhr.onabort = () => reject(new Error('Nahrávání bylo přerušeno. Zkuste to znovu.'));
          xhr.send(form);
        });
      } finally {
        videoRequest.current = null;
        if (mounted.current) setUploadingVideo(false);
      }
      if (data.status === 'pending') pendingApproval.current = true;
      return data;
    }
    form.append('profile_id', profileId);
    form.append('short_description', task.short || '');
    form.append('long_description', task.long || '');
    form.append('metadata', JSON.stringify({ type: task.type, ...(task.type === 'youtube' ? { youtubeUrl: video.url } : {}) }));
    if (task.file) form.append('image', task.file);
    const response = await fetch(managed ? `/api/profiles/${profileId}/memories` : '/api/memories', { method: 'POST', body: form, ...(managed ? { headers: profileHeaders(profileId) } : {}) });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(response.status === 429 ? 'Příliš mnoho příspěvků. Zkuste to prosím později.' : 'Odeslání se nepodařilo. Zkuste to znovu.');
    if (data.status === 'pending') pendingApproval.current = true;
    return data;
  };
  const submit = async (event) => {
    event.preventDefault();
    if (busy || !ready) return;
    if (!anonymous && (!author.trim() || author.trim().toLowerCase() === 'anonym')) {
      setError('Doplňte prosím své jméno.');
      return;
    }
    if (mode === 'text' && textType === 'quotation' && text.length > 2000) {
      setError('Citát může mít nejvýše 2000 znaků.');
      return;
    }
    setBusy(true);
    onBusyChange(true);
    setError('');
    setConfirmation('');
    setVideoProgress(0);
    if (mode === 'video') videoRef.current?.pause();
    const tasks =
      mode === 'image'
        ? remaining.map((photo) => ({ id: photo.id, file: photo.file, type: photo.type, short: photo.caption, long: caption }))
        : mode === 'video'
          ? [{ id: videoFile.id, file: videoFile.file, type: 'video', short: videoTitle.trim() }]
          : [{ id: draftKeys.current[mode === 'text' ? textType : 'youtube'], type: mode === 'text' ? textType : 'youtube', short: mode === 'text' ? (textType === 'quotation' ? text : title) : videoTitle, long: mode === 'text' ? (textType === 'quotation' ? title : text) : '' }];
    let failures = 0;
    let completed = 0;
    try {
      for (const task of tasks) {
        if (!mounted.current) break;
        mark(task.id, 'uploading');
        try {
          await post(task);
          sent.current.add(task.id);
          mark(task.id, 'sent');
          completed += 1;
        } catch (error) {
          failures += 1;
          mark(task.id, 'failed');
          if (mounted.current) setError(error.message);
        }
      }
      if (!mounted.current) return;
      if (failures) {
        if (completed) callback({ changed: true });
        return;
      }
      let success = pendingApproval.current ? 'Vzpomínky byly odeslány ke schválení.' : 'Vzpomínky byly přidány. Děkujeme.';
      if (sendEditLink && contributor.email) {
        try {
          const response = await fetch(`/api/profiles/${profileId}/contributor/email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ send: true }) });
          if (!response.ok) throw new Error();
          success += ' Odkaz k úpravám byl předán k odeslání e-mailem.';
        } catch { success += ' E-mail s odkazem se nepodařilo odeslat. Své příspěvky můžete upravit na tomto zařízení.'; }
      }
      setSendEditLink(false);
      setConfirmation(success);
      if (mode === 'image') {
        photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
        setPhotos([]);
        setCaption('');
      } else if (mode === 'text') {
        setText('');
        setTitle('');
      } else if (mode === 'video') {
        URL.revokeObjectURL(videoPreview.current);
        videoPreview.current = null;
        setVideoFile(null);
        setVideoTitle('');
      } else {
        setYoutubeUrl('');
        setVideoTitle('');
      }
      draftKeys.current = { story: createId(), quotation: createId(), youtube: createId() };
      sent.current.clear();
      pendingApproval.current = false;
      callback({ success, changed: true, close: true });
    } finally {
      if (mounted.current) setBusy(false);
      onBusyChange(false);
    }
  };

  return (
    <form className="memory-composer" onSubmit={submit}>
      {profileName && <p className="composer-profile">{profileName}</p>}
      <fieldset disabled={busy}>
        <div className={`composer-modes${allowVideo ? ' with-video' : ''}`} role="group" aria-label="Co chcete přidat?">
          {MODES.filter(([value]) => value !== 'video' || allowVideo).map(([value, label, Icon]) => (
            <button
              type="button"
              key={value}
              aria-pressed={mode === value}
              onClick={() => {
                setMode(value);
                setError('');
                setConfirmation('');
              }}
            >
              <Icon size={22} />
              <span>{value === 'image' && allowVideo ? 'Fotografie a videa' : label}</span>
            </button>
          ))}
        </div>
        {mode === 'image' && (
          <section className="composer-content" aria-label={allowVideo ? 'Fotografie a videa' : 'Fotografie'}>
            <input
              ref={input}
              className="composer-file-input"
              aria-label={allowVideo ? 'Vybrat fotografie a videa' : 'Vybrat fotografie'}
              tabIndex={-1}
              type="file"
              accept={`image/jpeg,image/png,image/webp,image/gif${allowVideo ? ',video/mp4,video/webm,.mp4,.m4v,.webm' : ''}`}
              multiple
              onChange={(event) => {
                addPhotos(Array.from(event.target.files || []));
                event.target.value = '';
              }}
            />
            {photos.length ? (
              <>
                <div
                  className={`composer-photos${dragging ? ' dragging' : ''}`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setDragging(false);
                    addPhotos(Array.from(event.dataTransfer.files));
                  }}
                >
                  {photos.map((photo, index) => (
                    <div className="composer-photo" key={photo.id}>
                      <div className="composer-photo-preview">
                        {photo.type === 'video' ? (
                          <>
                            <video src={photo.preview} aria-label={`Video ${index + 1}: ${photo.file.name}`} preload="metadata" muted playsInline />
                            <span className="composer-video-badge" title="Video">
                              <Video size={17} />
                            </span>
                          </>
                        ) : (
                          <img src={photo.preview} alt={`Fotografie ${index + 1}`} loading="lazy" decoding="async" />
                        )}
                        {photo.status === 'sent' ? (
                          <span className="composer-photo-status" title="Odesláno">
                            <Check size={19} />
                          </span>
                        ) : (
                          <button type="button" className="composer-remove" title={photo.type === 'video' ? 'Odebrat video' : 'Odebrat fotografii'} aria-label={`Odebrat ${photo.type === 'video' ? 'video' : 'fotografii'} ${index + 1}`} onClick={() => removePhoto(photo)}>
                            <X size={16} />
                          </button>
                        )}
                        {photo.status === 'uploading' && (
                          <span className="composer-photo-loading">
                            <LoaderCircle size={23} className="composer-spinner" />
                          </span>
                        )}
                        {photo.status === 'failed' && <span className="composer-photo-failed">Neodesláno</span>}
                      </div>
                      {editingCaption === photo.id ? (
                        <input
                          aria-label={`Popisek ${photo.type === 'video' ? 'videa' : 'fotografie'} ${index + 1}`}
                          autoFocus
                          maxLength={350}
                          value={photo.caption}
                          onChange={(event) => setPhotos((previous) => previous.map((item) => (item.id === photo.id ? { ...item, caption: event.target.value } : item)))}
                          onBlur={() => setEditingCaption(null)}
                        />
                      ) : (
                        <button type="button" className="composer-caption-button" disabled={photo.status === 'sent'} onClick={() => setEditingCaption(photo.id)}>
                          <Pencil size={13} />
                          <span>{photo.caption || 'Popisek'}</span>
                        </button>
                      )}
                    </div>
                  ))}
                  {(allowVideo || photos.length < maxPhotos) && (
                    <button type="button" className="composer-add-photo" title={allowVideo ? 'Přidat fotografie a videa' : 'Přidat fotografie'} aria-label={allowVideo ? 'Přidat fotografie a videa' : 'Přidat fotografie'} onClick={() => input.current.click()}>
                      <Plus size={27} />
                    </button>
                  )}
                </div>
                <label className="composer-field">
                  {photos.some((photo) => photo.type === 'video') ? 'Vzpomínka k souborům' : 'Vzpomínka k fotografiím'} <small>(nepovinné)</small>
                  <textarea aria-label={photos.some((photo) => photo.type === 'video') ? 'Vzpomínka k souborům' : 'Vzpomínka k fotografiím'} rows={2} maxLength={10000} value={caption} onChange={(event) => setCaption(event.target.value)} />
                </label>
              </>
            ) : (
              <button
                type="button"
                className={`composer-dropzone${dragging ? ' dragging' : ''}`}
                onClick={() => input.current.click()}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  addPhotos(Array.from(event.dataTransfer.files));
                }}
              >
                <ImagePlus size={37} />
                <strong>{allowVideo ? 'Vybrat fotografie a videa' : 'Vybrat fotografie'}</strong>
                <span>nebo je sem přetáhnout</span>
                <small>{allowVideo ? 'Fotografie do 12 MB · MP4, WebM do 512 MB' : 'JPG, PNG, WebP, GIF · do 12 MB'}</small>
              </button>
            )}
            {selectionError && (
              <p className="composer-error" role="alert">
                {selectionError}
              </p>
            )}
          </section>
        )}
        {mode === 'text' && (
          <section className="composer-content">
            <div className="composer-text-kind" role="group" aria-label="Typ textu">
              <button type="button" aria-pressed={textType === 'story'} onClick={() => setTextType('story')}>
                Příběh
              </button>
              <button type="button" aria-pressed={textType === 'quotation'} onClick={() => setTextType('quotation')}>
                Citát
              </button>
            </div>
            <label className="composer-field">
              {textType === 'story' ? 'Vaše vzpomínka' : 'Citát'}
              <textarea aria-label={textType === 'story' ? 'Vaše vzpomínka' : 'Citát'} rows={6} maxLength={textType === 'quotation' ? 2000 : 10000} value={text} onChange={(event) => setText(event.target.value)} />
            </label>
            <label className="composer-field">
              {textType === 'story' ? 'Nadpis' : 'Souvislost'} <small>(nepovinné)</small>
              <input maxLength={350} value={title} onChange={(event) => setTitle(event.target.value)} />
            </label>
          </section>
        )}
        {mode === 'youtube' && (
          <section className="composer-content">
            <label className="composer-field">
              Odkaz na YouTube
              <input type="text" inputMode="url" autoComplete="off" placeholder="https://www.youtube.com/watch?v=…" maxLength={2048} value={youtubeUrl} onChange={(event) => setYoutubeUrl(event.target.value)} />
            </label>
            {youtubeUrl.trim() && (
              <p className={video ? 'composer-link-valid' : 'composer-error'} role="status">
                {video ? (
                  <>
                    <Check size={16} /> Odkaz je platný
                  </>
                ) : (
                  'Vložte odkaz na konkrétní video na YouTube.'
                )}
              </p>
            )}
            <label className="composer-field">
              Název videa <small>(nepovinné)</small>
              <input maxLength={350} value={videoTitle} onChange={(event) => setVideoTitle(event.target.value)} />
            </label>
          </section>
        )}
        {mode === 'video' && allowVideo && (
          <section className="composer-content">
            <input
              ref={videoInput}
              className="composer-file-input"
              aria-label="Vybrat video"
              tabIndex={-1}
              type="file"
              accept="video/mp4,video/webm,.mp4,.m4v,.webm"
              onChange={(event) => {
                selectVideo(event.target.files?.[0]);
                event.target.value = '';
              }}
            />
            {!videoFile && (
              <button type="button" className="composer-dropzone" onClick={() => videoInput.current.click()}>
                <Video size={37} />
                <strong>Vybrat video</strong>
                <small>MP4 (H.264/AAC), WebM · do 512 MB</small>
              </button>
            )}
            {videoFile && (
              <>
                <video ref={videoRef} className="composer-video-preview" src={videoFile.preview} controls playsInline preload="metadata" />
                <p className="composer-video-name">{videoFile.file.name}</p>
                <div className="composer-video-actions">
                  <button type="button" className="site-button secondary" onClick={() => videoInput.current.click()}>
                    <Video size={17} /> Změnit video
                  </button>
                  <button
                    type="button"
                    className="site-icon"
                    title="Odebrat video"
                    aria-label="Odebrat video"
                    onClick={() => {
                      URL.revokeObjectURL(videoPreview.current);
                      videoPreview.current = null;
                      setVideoFile(null);
                    }}
                  >
                    <X size={18} />
                  </button>
                </div>
                <label className="composer-field">
                  Název videa <small>(nepovinné)</small>
                  <input maxLength={200} value={videoTitle} onChange={(event) => setVideoTitle(event.target.value)} />
                </label>
              </>
            )}
          </section>
        )}
        <label className="composer-field composer-author">
          Vaše jméno {anonymous && <small>(nepovinné)</small>}
          <input aria-label="Vaše jméno" autoComplete="name" maxLength={255} value={author} onChange={(event) => { contributorDirty.current = true; setAuthor(event.target.value); }} required={!anonymous} />
        </label>
        <ContributorFields value={contributor} onChange={(key, value) => { contributorDirty.current = true; setContributor(previous => ({ ...previous, [key]: value })); if (key === 'email') setSendEditLink(false); }} />
        {contributor.email && <label className="contributor-email-option">
          <input type="checkbox" checked={sendEditLink} disabled={!mailAvailable} onChange={event => setSendEditLink(event.target.checked)} />
          Poslat mi odkaz pro úpravu mých příspěvků
          {!mailAvailable && <small>Odesílání e-mailů není nyní dostupné.</small>}
        </label>}
      </fieldset>
      {error && (
        <p className="composer-error" role="alert">
          {error}
        </p>
      )}
      {confirmation && (
        <p className="composer-success" role="status">
          {confirmation}
        </p>
      )}
      {busy && mode === 'image' && (
        <div className="composer-progress" role="status">
          <progress max={photos.length} value={photos.filter((photo) => photo.status === 'sent').length} />
          <span>
            Odesláno {photos.filter((photo) => photo.status === 'sent').length} z {photos.length}
          </span>
        </div>
      )}
      {busy && uploadingVideo && (
        <div className="composer-progress" role="status">
          <progress max="100" value={videoProgress} />
          <span>{videoProgress < 100 ? `${videoProgress} %` : 'Kontroluji video…'}</span>
          <button type="button" className="site-icon" title="Zrušit nahrávání" aria-label="Zrušit nahrávání" onClick={() => videoRequest.current?.abort()}>
            <X size={18} />
          </button>
        </div>
      )}
      <footer className="composer-footer">
        <p>{profileSettings.moderationMode === 'moderated' ? 'Před zveřejněním schvaluje správce.' : ''}</p>
        <button className="site-button" type="submit" disabled={busy || !ready}>
          {busy ? <LoaderCircle size={17} className="composer-spinner" /> : <Send size={17} />}
          {busy ? 'Odesílám…' : photos.some((photo) => photo.status === 'failed') && mode === 'image' ? 'Zkusit znovu' : 'Odeslat vzpomínku'}
        </button>
      </footer>
    </form>
  );
}
